import { supabase } from '@/lib/supabase'
import type { Tables } from '@/types/database.types'

export type Activity = Tables<'activities'>
export type ActivityCategory = 'transport' | 'accommodation' | 'discovery' | 'meal'

export interface NewActivityInput {
  tripId: string
  dayId?: string
  title: string
  category?: ActivityCategory
  location?: string
  latitude?: number
  longitude?: number
  startTime?: string
  endTime?: string
  note?: string
}

export async function listActivitiesByDay(dayId: string): Promise<Activity[]> {
  const { data, error } = await supabase
    .from('activities')
    .select('*')
    .eq('day_id', dayId)
    .order('start_time', { nullsFirst: false })
  if (error) throw error
  return data
}

export async function listUnscheduledActivities(tripId: string): Promise<Activity[]> {
  const { data, error } = await supabase
    .from('activities')
    .select('*')
    .eq('trip_id', tripId)
    .is('day_id', null)
    .order('start_time', { nullsFirst: false })
  if (error) throw error
  return data
}

export async function listActivitiesByTrip(tripId: string): Promise<Activity[]> {
  const { data, error } = await supabase.from('activities').select('*').eq('trip_id', tripId)
  if (error) throw error
  return data
}

export async function createActivity(input: NewActivityInput): Promise<Activity> {
  const { data, error } = await supabase
    .from('activities')
    .insert({
      trip_id: input.tripId,
      day_id: input.dayId ?? null,
      title: input.title,
      category: input.category,
      location: input.location,
      latitude: input.latitude,
      longitude: input.longitude,
      start_time: input.startTime,
      end_time: input.endTime,
      note: input.note,
    })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function assignActivityToDay(activityId: string, dayId: string): Promise<Activity> {
  const { data, error } = await supabase
    .from('activities')
    .update({ day_id: dayId })
    .eq('id', activityId)
    .select()
    .single()
  if (error) throw error
  return data
}

export interface ActivityPatch {
  title: string
  category?: ActivityCategory
  location?: string
  latitude?: number
  longitude?: number
  startTime?: string
  endTime?: string
  note?: string
}

export async function updateActivity(activityId: string, patch: ActivityPatch): Promise<Activity> {
  const { data, error } = await supabase
    .from('activities')
    .update({
      title: patch.title,
      category: patch.category ?? null,
      location: patch.location ?? null,
      latitude: patch.latitude ?? null,
      longitude: patch.longitude ?? null,
      start_time: patch.startTime ?? null,
      end_time: patch.endTime ?? null,
      note: patch.note ?? null,
    })
    .eq('id', activityId)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteActivity(activityId: string): Promise<void> {
  const { error } = await supabase.from('activities').delete().eq('id', activityId)
  if (error) throw error
}
